/**
 * อ่านไฟล์ export ของ Tax Log V2 (ทั้ง session ในไฟล์ JSON เดียว) และทำ "API ปลอม" ให้หน้า
 * `/TaxLogV2/offline` เปิดดูได้โดยไม่ต้องต่อ server
 * ══════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ไฟล์มาจาก `GET api/v1/TaxLogV2/sessions/{sessionId}/export` (`TaxLogV2SessionExportDto` ฝั่ง BE)
 * — envelope `{ kind, schemaVersion, exportedAt, exportedBy, source, session, forms, turns,
 * turnDetails, formStates, warnings }` โดย `session`/`forms`/`turns`/`turnDetails[*]`/`formStates[*]`
 * มีรูปเดียวกับที่ endpoint อ่านแต่ละตัวคืน ⇒ หน้า Detail ใช้ component เดิมได้ทั้งหมด
 *
 * 🔴 **ไม่มี import ใด ๆ โดยเจตนา** — ไฟล์นี้ถูกเทสต์ด้วย Node เปล่า ๆ
 * (`tests/taxLogOfflineFile.test.mjs`) ⇒ ห้ามดึง vue / `import.meta.env` / service เข้ามา
 *
 * 🔴 **adapter เป็น read-only** — `createOfflineTaxLogApi()` เลียน contract ของ `services/taxLogV2Api.js`
 * เฉพาะ 5 method ที่หน้า Detail เรียก (คืน Promise, 404 ⇒ `err.httpStatus === 404`) และ **ไม่ mutate
 * ไฟล์** ไม่ว่ากรณีใด — ไฟล์เดียวกันถูกเปิดซ้ำได้ตลอดอายุของหน้า
 *
 * ⚠️ เวลาในไฟล์ (`exportedAt`, `createdDate` ของแถวต่าง ๆ) เป็น UTC ที่ **อาจไม่มี `Z`** แบบเดียวกับบน wire
 * ⇒ คง string เดิมไว้ ให้ `utils/thaiTime.js → parseUtcDate` จัดการตอนแสดงผลเหมือนหน้า live
 */

/** `TaxLogV2SessionExportDto.ExportKind` — ต้องตรงกับ BE ทุกตัวอักษร */
export const TAXLOG_EXPORT_KIND = 'lhb.taxlog.v2.session-export';

/** major ของ `schemaVersion` ที่อ่านได้ — BE เขียน "1.0"; minor ที่เพิ่มขึ้น (1.x) ต้องเข้ากันได้ย้อนหลัง */
export const TAXLOG_EXPORT_SCHEMA_MAJOR = '1';

/** ข้อความกลางของ error "ไม่ใช่ไฟล์ของเรา" */
const NOT_OUR_FILE = 'ไม่ใช่ไฟล์ export ของ Tax Log';

/** กันชื่อ kind ยาวผิดปกติ (ไฟล์มั่ว) ทำให้ข้อความ error ล้นจอ */
const MAX_KIND_IN_MESSAGE = 80;

/** ความยาวสูงสุดของ sessionId ในชื่อไฟล์ — กันชนเพดานชื่อไฟล์ของ OS (255) */
const MAX_SESSION_IN_FILE_NAME = 80;

/**
 * date-time ที่ **ไม่มี** zone — ตัวเดียวกับ `ZONELESS_DATETIME` ใน `utils/thaiTime.js`
 * (ลอกมาเพราะไฟล์นี้ห้าม import) ⇒ BE หมายถึง UTC เสมอ ให้เติม `Z` ก่อน parse
 */
const ZONELESS_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasOwn(obj, key) {
  return isPlainObject(obj) && Object.prototype.hasOwnProperty.call(obj, key);
}

function fail(error) {
  return { ok: false, error };
}

/**
 * อ่านข้อความในไฟล์ → envelope ที่ normalize แล้ว
 *
 * ลำดับการตรวจ (ข้อแรกที่ล้มชนะ): JSON → เป็น object → `kind` → `schemaVersion` → โครงที่หน้า Detail ต้องใช้
 * · `forms`/`turns` ที่เป็น null/ไม่มี ⇒ `[]` · `turnDetails`/`formStates` ที่เป็น null/ไม่มี ⇒ `{}`
 *   แต่ถ้ามีมาแล้ว **ผิดชนิด** (เช่น `turns` เป็น object) ⇒ error — ไฟล์เสีย ไม่ใช่ไฟล์ว่าง
 * · `warnings` ที่ไม่ใช่ array ⇒ `[]` (เป็นข้อมูลประกอบ ไม่ควรทำให้เปิดไฟล์ไม่ได้)
 *
 * @param {string|Object} text เนื้อไฟล์ (string จาก `FileReader.readAsText`) หรือ object ที่ parse แล้ว
 *   — ถ้าเป็น object จะ **ไม่ถูก mutate** (คืน object ใหม่แบบตื้น)
 * @returns {{ ok: true, file: Object } | { ok: false, error: string }}
 */
export function parseTaxLogExport(text) {
  let obj = text;
  if (typeof text === 'string') {
    try {
      // ตัด BOM หัวไฟล์ (โปรแกรมบางตัวเติมตอนบันทึกซ้ำ) — `JSON.parse` ไม่รับ
      obj = JSON.parse(text.replace(/^﻿/, ''));
    } catch (e) {
      return fail('ไฟล์นี้ไม่ใช่ JSON ที่อ่านได้');
    }
  }

  if (!isPlainObject(obj)) return fail(`ไฟล์นี้${NOT_OUR_FILE}`);

  const kind = obj.kind;
  if (kind === undefined || kind === null || String(kind).trim() === '') {
    return fail(`ไฟล์นี้ไม่มี kind — ${NOT_OUR_FILE}`);
  }
  if (kind !== TAXLOG_EXPORT_KIND) {
    return fail(`ไฟล์นี้เป็น ${String(kind).slice(0, MAX_KIND_IN_MESSAGE)} ${NOT_OUR_FILE}`);
  }

  const version = obj.schemaVersion === undefined || obj.schemaVersion === null
    ? ''
    : String(obj.schemaVersion).trim();
  if (version.split('.')[0] !== TAXLOG_EXPORT_SCHEMA_MAJOR) {
    return fail(`ไฟล์เวอร์ชัน ${version || '(ไม่ระบุ)'} ไม่รองรับ (รองรับ ${TAXLOG_EXPORT_SCHEMA_MAJOR}.x)`);
  }

  const session = obj.session;
  if (!isPlainObject(session) || typeof session.sessionID !== 'string' || !session.sessionID.trim()) {
    return fail('ไฟล์นี้ไม่มี session.sessionID — เปิดดูไม่ได้');
  }

  const arrayField = (name) => (obj[name] === undefined || obj[name] === null ? [] : obj[name]);
  const objectField = (name) => (obj[name] === undefined || obj[name] === null ? {} : obj[name]);

  const forms = arrayField('forms');
  const turns = arrayField('turns');
  const turnDetails = objectField('turnDetails');
  const formStates = objectField('formStates');
  if (!Array.isArray(forms)) return fail('ไฟล์นี้ผิดรูปแบบ — forms ต้องเป็น array');
  if (!Array.isArray(turns)) return fail('ไฟล์นี้ผิดรูปแบบ — turns ต้องเป็น array');
  if (!isPlainObject(turnDetails)) return fail('ไฟล์นี้ผิดรูปแบบ — turnDetails ต้องเป็น object');
  if (!isPlainObject(formStates)) return fail('ไฟล์นี้ผิดรูปแบบ — formStates ต้องเป็น object');

  return {
    ok: true,
    file: {
      ...obj,
      session,
      forms,
      turns,
      turnDetails,
      formStates,
      warnings: Array.isArray(obj.warnings) ? obj.warnings : [],
    },
  };
}

/** Error แบบเดียวกับที่ `taxLogV2Api` โยนตอน 404 — หน้า Detail แยกเคสด้วย `err.httpStatus === 404` */
function notFound(message) {
  const err = new Error(message);
  err.httpStatus = 404;
  err.offline = true;
  return err;
}

function lookup(map, key, message) {
  return hasOwn(map, key) && map[key] != null
    ? Promise.resolve(map[key])
    : Promise.reject(notFound(message));
}

/**
 * "API" อ่านอย่างเดียวที่ตอบจากไฟล์ — method ชื่อ/ลายเซ็น/รูปผลลัพธ์เดียวกับ `taxLogV2Api` ที่หน้า Detail เรียก
 *
 * · `listTurns` คืน **ทุกเทิร์นในหน้าแรก** (ไฟล์มีครบอยู่แล้ว) และหน้า >1 คืน `items: []` พร้อม `totalCount`
 *   เต็ม ⇒ `hasMoreTurns` ของหน้า Detail เป็น false ทันที ไม่มีการโหลดเพิ่ม
 * · key ที่ไม่มีในไฟล์ ⇒ reject Error ที่มี `httpStatus === 404` (ไม่ใช่ resolve null) ให้หน้าเดิมแสดง "ไม่พบ"
 * · array ที่คืนเป็นสำเนาตื้น — ผู้เรียกจะ push/splice อย่างไรก็ไม่กระทบไฟล์
 *
 * @param {Object} file envelope จาก `parseTaxLogExport(...).file`
 * @returns {{ isOffline: true, getSession: Function, listTurns: Function, getFormState: Function,
 *   getProductionTurn: Function, getPlaygroundTurn: Function }}
 */
export function createOfflineTaxLogApi(file) {
  const f = isPlainObject(file) ? file : {};
  const forms = Array.isArray(f.forms) ? f.forms : [];
  const turns = Array.isArray(f.turns) ? f.turns : [];
  const turnDetails = isPlainObject(f.turnDetails) ? f.turnDetails : {};
  const formStates = isPlainObject(f.formStates) ? f.formStates : {};

  return Object.freeze({
    isOffline: true,

    /** sessionId ถูกละไว้ — adapter ผูกกับไฟล์เดียว (session เดียว) อยู่แล้ว */
    getSession() {
      return Promise.resolve({ session: f.session || null, forms: forms.slice() });
    },

    listTurns(sessionId, page = 1) {
      const p = Number(page) || 1;
      return Promise.resolve({
        items: p <= 1 ? turns.slice() : [],
        totalCount: turns.length,
      });
    },

    getFormState(formId) {
      return lookup(formStates, String(formId), 'ไม่พบ form state ในไฟล์');
    },

    getProductionTurn(logMessageId) {
      return lookup(turnDetails, `lm:${logMessageId}`, 'ไม่พบรายละเอียดเทิร์นนี้ในไฟล์');
    },

    getPlaygroundTurn(turnId) {
      return lookup(turnDetails, `pg:${turnId}`, 'ไม่พบรายละเอียดเทิร์นนี้ในไฟล์');
    },
  });
}

/** `exportedAt` → Date (UTC) · ไม่มี zone ⇒ เติม `Z` · parse ไม่ได้ ⇒ null */
function parseExportedAt(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const iso = ZONELESS_DATETIME.test(raw) ? `${raw.replace(' ', 'T')}Z` : raw;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * ชื่อไฟล์สำหรับดาวน์โหลด: `taxlog-<sessionId>-<yyyyMMdd-HHmmss>.json`
 * · เวลาเป็น **UTC** จาก `exportedAt` (ไม่ใช่เวลาเครื่อง — ชื่อไฟล์ต้องเหมือนกันไม่ว่าใครกดส่งออก)
 *   parse ไม่ได้ ⇒ ใช้เวลาปัจจุบัน
 * · sessionId ถูกทำให้ปลอดภัย: ทุกช่วงของอักขระนอก `[A-Za-z0-9._-]` ⇒ `-` (ตัด `-` หัว/ท้าย, ว่าง ⇒ `session`)
 *
 * @param {Object} file envelope (หรือ `data` ที่ `taxLogV2Api.exportSession` คืน)
 * @returns {string}
 */
export function buildTaxLogExportFileName(file) {
  const f = isPlainObject(file) ? file : {};
  const rawId = (isPlainObject(f.session) && f.session.sessionID)
    || (isPlainObject(f.source) && f.source.sessionId)
    || '';
  const safeId = String(rawId)
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SESSION_IN_FILE_NAME) || 'session';

  const d = parseExportedAt(f.exportedAt) || new Date();
  const stamp = `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}`
    + `-${pad2(d.getUTCHours())}${pad2(d.getUTCMinutes())}${pad2(d.getUTCSeconds())}`;

  return `taxlog-${safeId}-${stamp}.json`;
}

/**
 * ข้อมูลสำหรับ banner "กำลังดูจากไฟล์" ของหน้า offline — ค่าที่ไม่มีในไฟล์เป็น `null` (ไม่ใช่ "null"/"")
 * · `exportedAt` คืน string เดิม ให้ผู้เรียกแปลงเป็นเวลาไทยด้วย helper เดียวกับหน้า live
 * · `turnCount`/`formCount` นับจากข้อมูลจริงในไฟล์ ไม่เชื่อ `source.turnCount` (ไฟล์อาจถูกแก้มือ)
 *
 * @param {Object} file envelope จาก `parseTaxLogExport(...).file`
 * @returns {{ sessionId: string|null, exportedAt: string|null, exportedBy: string|null,
 *   environment: string|null, turnSource: string|null, turnCount: number, formCount: number,
 *   warnings: string[] }}
 */
export function describeTaxLogExport(file) {
  const f = isPlainObject(file) ? file : {};
  const source = isPlainObject(f.source) ? f.source : {};
  const session = isPlainObject(f.session) ? f.session : {};
  return {
    sessionId: session.sessionID || source.sessionId || null,
    exportedAt: f.exportedAt ?? null,
    exportedBy: f.exportedBy ?? null,
    environment: source.environment ?? null,
    turnSource: source.turnSource ?? null,
    turnCount: Array.isArray(f.turns) ? f.turns.length : 0,
    formCount: Array.isArray(f.forms) ? f.forms.length : 0,
    warnings: Array.isArray(f.warnings) ? f.warnings.slice() : [],
  };
}
